import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import LiveBettingFilters from '../components/LiveBettingFilters'
import LiveBetTable from '../components/LiveBetTable'
import BettingSummary, { ParlayTotalsBySize } from '../components/BettingSummary'
import BetDetailsModal from '../components/BetDetailsModal'
import RejectConfirmationModal from '../components/RejectConfirmationModal'
import { liveBetStatusOptions } from '../../../utils/liveBettingDemoData'
import bettingService from '../../../services/bettingService'
import { LoadingSpinner } from '../../../components'
import { getFootballDateRange } from '../../../utils/dateUtils'
import { useWindowWidth } from '@react-hook/window-size';

const LiveBetsFeedPage = () => {
  const onlyWidth = useWindowWidth();
  const [statusFilter, setStatusFilter] = useState(liveBetStatusOptions[0])
  const [selectedDate, setSelectedDate] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('time-desc')
  const [liveBets, setLiveBets] = useState([])
  //const [largeBets, setLargeBets] = useState([])
  const [parlayTotals, setParlayTotals] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isDetailsLoading, setIsDetailsLoading] = useState(false)
  const [ticketEvents, setTicketEvents] = useState([])
  const [detailsError, setDetailsError] = useState(null)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [bettingTotals, setBettingTotals] = useState({
    singleTickets: 0,
    parlayTickets: 0,
    exposure: 0,
    avgStake: 0,
  })
  const isActiveRef = useRef(true)
  const isFetchingRef = useRef(false)

  useEffect(() => {
    return () => {
      isActiveRef.current = false
    }
  }, [])

  const filteredTickets = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const filtered = liveBets.filter((ticket) => {
      if (
        statusFilter.value !== 'all' &&
        String(ticket.status).toLowerCase() !== statusFilter.value.toLowerCase()
      ) {
        return false
      }

      if (selectedDate) {
        const bettedAt = new Date(ticket.placedAt)
        if (Number.isNaN(bettedAt.getTime())) return false
        const localDate = [
          bettedAt.getFullYear(),
          String(bettedAt.getMonth() + 1).padStart(2, '0'),
          String(bettedAt.getDate()).padStart(2, '0'),
        ].join('-')
        if (localDate !== selectedDate) return false
      }

      if (normalizedQuery) {
        const voucherNo = String(ticket.id ?? '').toLowerCase()
        const username = String(ticket.user ?? '').toLowerCase()
        if (!voucherNo.includes(normalizedQuery) && !username.includes(normalizedQuery)) {
          return false
        }
      }

      return true
    })

    return filtered.sort((first, second) => {
      switch (sortBy) {
        case 'time-asc':
          return new Date(first.placedAt).getTime() - new Date(second.placedAt).getTime()
        case 'amount-desc':
          return (Number(second.stake) || 0) - (Number(first.stake) || 0)
        case 'amount-asc':
          return (Number(first.stake) || 0) - (Number(second.stake) || 0)
        case 'type-asc':
          return String(first.type ?? '').localeCompare(String(second.type ?? ''))
        case 'type-desc':
          return String(second.type ?? '').localeCompare(String(first.type ?? ''))
        case 'time-desc':
        default:
          return new Date(second.placedAt).getTime() - new Date(first.placedAt).getTime()
      }
    })
  }, [liveBets, searchQuery, selectedDate, sortBy, statusFilter])

  const fetchLiveBets = useCallback(async () => {
    if (isFetchingRef.current || !isActiveRef.current) return
    isFetchingRef.current = true
    if (isActiveRef.current) {
      setIsLoading(true)
    }

    try {
      const payload = {
        searchDate: await getFootballDateRange()
      }

      const res = await bettingService.getLiveBettingMatch(payload)
      const reports = res?.data?.bettingReports

      if (!reports || res.status !== 200) {
        throw new Error('Failed to load live betting data')
      }

      const formattedLiveBetsData = (reports.largeBettings || []).map((bet) => ({
        gamblingId : bet.gamblingId,
        id: bet.postingNo,
        placedAt: bet.createdDate,
        user: bet.userName,
        type: bet.gamblingType,
        stake: bet.betAmount,
        potential: bet.potentials,
        status: bet.status,
        channel: 'mobile',
      }))

      // const formattedLargeBetsData = (reports.largeBettings || []).map((bet) => ({
      //   id: bet.postingNo,
      //   placedAt: bet.createdDate,
      //   user: bet.userName,
      //   type: bet.gamblingType,
      //   stake: bet.betAmount,
      //   potential: bet.potentials,
      //   status: bet.status,
      //   channel: 'mobile',
      // }))

      setParlayTotals(reports.mixTypes || [])

      if (!isActiveRef.current) return

      setLiveBets(formattedLiveBetsData)
      //setLargeBets(formattedLargeBetsData)
      setBettingTotals({
        singleTickets: reports.singleVouchers ?? 0,
        parlayTickets: reports.mixVouchers ?? 0,
        exposure: reports.totalPotentials ?? 0,
        avgStake: reports.totalStakes ?? 0,
      })
    } catch (error) {
      if (isActiveRef.current) {
        console.error('Failed to fetch live betting data', error)
        setLiveBets([])
        //setLargeBets([])
        setBettingTotals({
          singleTickets: 0,
          parlayTickets: 0,
          exposure: 0,
          avgStake: 0,
        })
      }
    } finally {
      if (isActiveRef.current) {
        setIsLoading(false)
      }
      isFetchingRef.current = false
    }
  }, [])

  const loadTicketDetails = useCallback(async (gamblingId) => {
    console.log('Loading ticket details for', gamblingId)
    if (!gamblingId) {
      setTicketEvents([])
      setDetailsError(null)
      return
    }

    setIsDetailsLoading(true)
    setDetailsError(null)

    try {
      const response = await bettingService.getBetDetails(gamblingId)

      const rootCandidates = [
        response?.data,
        response?.data?.voucherDetails,
        response?.data?.data,
      ]

      const candidateKeys = [
        'events',
        'voucherEvents',
        'legs',
        'details',
        'singleVoucherEvents',
        'eventDetails',
        'matches',
        'selections',
      ]

      let rawEvents = []

      for (const root of rootCandidates) {
        if (!root) continue

        if (Array.isArray(root) && root.length) {
          rawEvents = root
          break
        }

        if (typeof root === 'object') {
          for (const key of candidateKeys) {
            const candidate = root[key]
            if (Array.isArray(candidate) && candidate.length) {
              rawEvents = candidate
              break
            }
          }
        }

        if (rawEvents.length) {
          break
        }
      }

      if (!rawEvents.length) {
        rawEvents = []
      }

      const normalizedEvents = rawEvents.map((event, index) => ({
        id: event.id ?? event.gamblingId,
        league: event.league ?? event.leagueName ?? event.leagueShort ?? 'Unknown league',
        home: event.home ?? event.homeTeam ?? event.homeTeamName ?? '-',
        away: event.away ?? event.awayTeam ?? event.awayTeamName ?? '-',
        eventTime: event.eventTime ?? event.kickOff ?? event.createdDate ?? null,
        result: event.result ?? event.eventResult ?? event.status ?? 'Pending',
        odds : event.odds ?? event.odd ?? event.price ?? '-',
        bettedColor : event.bettedColor ?? event.betColor ?? 'inherit',
        bettedTeam: event.bettedTeam ?? event.betTeam ?? event.selection ?? event.pick ?? event.side ?? '-',
      }))

      setTicketEvents(normalizedEvents)
    } catch (error) {
      console.error('Failed to fetch bet details', error)
      setDetailsError(error)
      setTicketEvents([])
    } finally {
      setIsDetailsLoading(false)
    }
  }, [])

  const handleViewTicket = useCallback(
    (ticket) => {
      if (!ticket?.id) return
      setSelectedTicket(ticket)
      setTicketEvents([])
      setDetailsError(null)
      setIsDetailsModalOpen(true)
      loadTicketDetails(ticket.gamblingId)
    },
    [loadTicketDetails],
  )

  const handleRetryDetails = useCallback(() => {
    if (selectedTicket?.gamblingId) {
      loadTicketDetails(selectedTicket.gamblingId)
    }
  }, [selectedTicket, loadTicketDetails])

  const handleCloseDetails = useCallback(() => {
    setIsDetailsModalOpen(false)
  }, [])

  const handleRejectTicket = useCallback((ticket) => {
    if (!ticket?.gamblingId) return
    setSelectedTicket(ticket)
    setIsRejectModalOpen(true)
  }, [])

  const handleConfirmReject = useCallback(async () => {
    if (!selectedTicket?.gamblingId) return

    setIsRejecting(true)

    try {
      await new Promise((resolve) => setTimeout(resolve, 600))
      console.log('Rejected ticket', selectedTicket.gamblingId)
    } catch (error) {
      console.error('Failed to reject ticket', error)
    } finally {
      setIsRejecting(false)
      setIsRejectModalOpen(false)
    }
  }, [selectedTicket])

  const handleCloseReject = useCallback(() => {
    setIsRejecting(false)
    setIsRejectModalOpen(false)
  }, [])

  useEffect(() => {
    fetchLiveBets()
  }, [fetchLiveBets])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="flex flex-col gap-1">
      <header className="">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-950 dark:text-white">
            Outstanding Live Betting Report
          </h1>
        </header>

      <BettingSummary totals={bettingTotals} />

      <LiveBettingFilters
        statusOptions={liveBetStatusOptions}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(280px,1fr)]">
        <LiveBetTable
          data={filteredTickets}
          onViewTicket={handleViewTicket}
          onRejectTicket={handleRejectTicket}
          isMobile={onlyWidth < 768}
        />
        <ParlayTotalsBySize parlayTotals={parlayTotals} />
      </div>

      {/* <section className="grid gap-3 rounded-2xl border border-dashed border-customAccent/30 bg-customSlate/60 p-4 text-sm text-customSecondaryText shadow-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted">
        <h4 className="text-base font-semibold text-customPrimaryText dark:text-customHeading">
          Operational playbook ideas
        </h4>
        <ul className="grid gap-2 md:grid-cols-3">
          <li className="rounded-xl bg-white p-3 font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading">
            Auto-highlight tickets that change odds &gt; 10% within 2 minutes to signal suspicious feeds.
          </li>
          <li className="rounded-xl bg-white p-3 font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading">
            Allow quick escalation to fraud desk with one click and prefilled context notes.
          </li>
          <li className="rounded-xl bg-white p-3 font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading">
            Surface correlated events (same user + market) to pre-empt arbitrage clusters.
          </li>
        </ul>
      </section> */}

      <BetDetailsModal
        open={isDetailsModalOpen}
        onClose={handleCloseDetails}
        ticketId={selectedTicket?.id}
        events={ticketEvents}
        isLoading={isDetailsLoading}
        error={detailsError}
        onRetry={handleRetryDetails}
      />

      <RejectConfirmationModal
        open={isRejectModalOpen}
        onClose={handleCloseReject}
        onConfirm={handleConfirmReject}
        ticketId={selectedTicket?.id}
        isSubmitting={isRejecting}
      />
    </div>
  )
}

export default LiveBetsFeedPage
